import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { extractErrorMessage } from "@/marketing/lib/errorMessage";
import { Button } from "@/marketing/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/marketing/components/ui/dialog";
import { campaignPipelineAPIClient } from "@/marketing/infrastructure/influencer/campaign/pipeline/campaignPipelineAPIClient";
import { CAMPAIGN_PIPELINE_KEYS } from "@/marketing/infrastructure/influencer/campaign/pipeline/utils/keys";
import { CAMPAIGN_ACTIVITY_KEYS } from "@/marketing/infrastructure/influencer/campaign/activity/utils/keys";
import type { CampaignInfluencer } from "@/marketing/models/campaignInfluencer";
import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";
import { useAuthStore } from "@/marketing/stores/useAuthStore";
import { ProductChoiceGrid } from "./ProductChoiceGrid";
import { buildCampaignProductSelectionState } from "./campaignProductSelection";

interface ProductChoicePickerProps {
  campaignId: string;
  campaign: InfluencerCampaign;
  creator: CampaignInfluencer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProductChoicePicker({
  campaignId,
  campaign,
  creator,
  open,
  onOpenChange,
}: ProductChoicePickerProps) {
  const queryClient = useQueryClient();
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
  const setBy = useAuthStore((state) => state.user?.email) ?? "ss-admin";
  const creatorLabel = creator.handle ?? creator.influencerId;
  const productSelectionState = buildCampaignProductSelectionState(
    campaign,
    selectedSkus,
  );

  const assignMutation = useMutation({
    mutationFn: () =>
      campaignPipelineAPIClient.setCampaignInfluencerSelectedSkus(campaignId, creator.influencerId, {
        selectedSkus,
        setBy,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CAMPAIGN_PIPELINE_KEYS.lists() });
      await queryClient.invalidateQueries({ queryKey: CAMPAIGN_ACTIVITY_KEYS.all });
      toast.success(`Assigned ${selectedSkus.length} product(s) to @${creatorLabel}`);
      onOpenChange(false);
    },
    onError: (assignError: unknown) =>
      toast.error(extractErrorMessage(assignError, "Could not assign products")),
  });

  const canAssign =
    productSelectionState.isComplete && !assignMutation.isPending;

  const handleCancel = () => onOpenChange(false);
  const handleAssign = () => assignMutation.mutate();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:tw-max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign products</DialogTitle>
          <DialogDescription>
            Pick the required products for @{creatorLabel} ({selectedSkus.length}/
            {productSelectionState.totalChoiceCount} selected). This creator did
            not choose their own — the assignment is logged to their timeline.
          </DialogDescription>
        </DialogHeader>

        <ProductChoiceGrid
          campaign={campaign}
          selectedSkus={selectedSkus}
          onChange={setSelectedSkus}
        />

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button disabled={!canAssign} onClick={handleAssign}>
            {assignMutation.isPending ? "Assigning…" : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
